import { Component, ElementRef, OnInit, ViewChild, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIcon } from '@ng-icons/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SharedUiModule } from '../../ui/shared-ui.module';

// Simple SHA512 implementation using Web Crypto API
async function sha512(message: string): Promise<string> {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-512', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

@Component({
    selector: 'app-form-card-register',
    templateUrl: './form-card-register.component.html',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        NgIcon,
        CommonModule,
        RouterModule,
        SharedUiModule,
    ],
    styleUrls: ['./form-card-register.component.css']
})
export class FormCardRegisterComponent implements OnInit {

    @ViewChild("dialog", { static: true }) dialog: ElementRef | undefined;

    myForm: FormGroup;
    isLoading = false;
    orderLink: string | null = null;
    errorMessage: string | null = null;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly http: HttpClient,
        private readonly renderer: Renderer2,
    ) {
        let autoOrderId = 'SCSP' + (new Date().getTime());
        this.myForm = this.formBuilder.group({
            // API Configuration
            'apiKey': ['5622e78f-d179-4005-b3e1-80b605bab465', Validators.required],
            'secret': ['213b0c6eef424d689ec2a509f87c4d40', Validators.required],
            'environment': ['QA'],

            // Order Information
            'orderNumber': [autoOrderId, Validators.required],
            'expirationDate': ['', Validators.required],
            'integrationMode': [null],

            // Customer Information
            'customerName': ['Sergio', Validators.required],
            'customerLastName': ['Picard', Validators.required],
            'customerPhone': ['999555666', Validators.required],
            'customerEmail': ['test@test.com', Validators.required],
            'customerDocumentType': ['DNI', Validators.required],
            'customerDocumentNumber': ['44556677', Validators.required],
            'customerIdentifier': ['a123456a-d123-4e3a-bcf4-aea1234f5f93', Validators.required],
            // Address Information
            'addressCountry': ['PER', Validators.required],
            'addressLevel1': ['LIMA', Validators.required],
            'addressLevel2': ['LIMA', Validators.required],
            'addressLevel3': ['SAN ISIDRO', Validators.required],
            'addressLine1': ['Ca Nueve 1802', Validators.required],
            'addressZip': ['15036', Validators.required],

            // Language
            'language': ['es_PE', Validators.required]
        });
    }

    ngOnInit(): void {
        // Set default expiration date to 6 months from now
        const defaultDate = new Date();
        defaultDate.setMonth(defaultDate.getMonth() + 6);
        this.myForm.patchValue({
            expirationDate: this.formatDateInputValue(defaultDate),
        });
    }

    private formatDateInputValue(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    private expirationDateToIso(value: string | Date): string {
        if (value instanceof Date) {
            return value.toISOString();
        }
        return new Date(`${value}T12:00:00`).toISOString();
    }

    async generateOrder() {
        if (this.myForm.valid) {
            this.isLoading = true;
            this.errorMessage = null;
            this.orderLink = null;

            const formValue = this.myForm.value;

            // Prepare the request body
            const requestBody = {
                order: {
                    number: formValue.orderNumber,
                    type: "ORDER",
                    country: {
                        code: formValue.addressCountry
                    },
                    currency: {
                        code: "PEN"
                    },
                    cardRegister: true,
                    ...(formValue.integrationMode !== null && formValue.integrationMode !== undefined && { integrationMode: Number(formValue.integrationMode) }),
                    multipleCustomerIndicator: false,
                    customer: {
                        identifier: formValue.customerIdentifier,
                        name: formValue.customerName,
                        lastName: formValue.customerLastName,
                        phone: formValue.customerPhone,
                        email: formValue.customerEmail,
                        document: {
                            type: formValue.customerDocumentType,
                            number: formValue.customerDocumentNumber
                        },
                        address: {
                            country: formValue.addressCountry,
                            levels: [
                                formValue.addressLevel1,
                                formValue.addressLevel2,
                                formValue.addressLevel3
                            ],
                            line1: formValue.addressLine1,
                            zip: formValue.addressZip
                        }
                    }
                },
                settings: {
                    expiration: {
                        type: "DATE",
                        date: this.expirationDateToIso(formValue.expirationDate)
                    },
                    language: formValue.language
                }
            };

            try {
                // Generate authentication headers
                const signedElement = formValue.orderNumber + 'PEN' + 1.0;
                const signature = await this.generateSignature(formValue.apiKey, signedElement, formValue.secret);

                const headers = new HttpHeaders({
                    'Content-Type': 'application/json',
                    'identifier': formValue.apiKey,
                    'signedElement': signedElement,
                    'signature': signature
                });

                // Determine base URL based on environment
                let baseUrl: string;
                switch (formValue.environment) {
                    case 'QA':
                        baseUrl = 'https://qas.api.epago.pe';
                        break;
                    case 'DEV':
                        baseUrl = 'https://dev.api.epago.pe';
                        break;
                    case 'LOCAL':
                        baseUrl = 'http://localhost:3001';
                        break;
                    default:
                        baseUrl = 'https://qas.api.epago.pe';
                }

                const url = `${baseUrl}/orders/create`;

                this.http.post(url, requestBody, { headers }).subscribe({
                    next: (response: any) => {
                        this.isLoading = false;
                        if (response.success && response.data?.order?.link) {
                            this.orderLink = response.data.order.link;
                            this.openCardRegisterModal();
                        } else {
                            this.errorMessage = 'Error al crear la orden: ' + (response.message?.text || 'Respuesta inesperada');
                        }
                    },
                    error: (error) => {
                        this.isLoading = false;
                        console.error('Error creating order:', error);
                        this.errorMessage = 'Error al crear la orden: ' + (error.error?.message?.text || error.message || 'Error de conexión');
                    }
                });
            } catch (error) {
                this.isLoading = false;
                console.error('Error generating signature:', error);
                this.errorMessage = 'Error al generar la firma: ' + error;
            }
        }
    }

    async generateSignature(apiKey: string, signedElement: string, secret: string): Promise<string> {
        // Generate SHA512 hash of apiKey + signedElement + secret
        const valueToHash = apiKey + signedElement + secret;
        return await sha512(valueToHash)
    }

    openCardRegisterModal() {
        if (this.orderLink) {
            const url = this.appendCardRegisteredQuery(this.orderLink);
            window.open(url, '_blank');
        }
    }

    appendCardRegisteredQuery(link: string): string {
        try {
            const url = new URL(link);
            url.searchParams.set('cardRegistered', 'true');
            return url.toString();
        } catch {
            const separator = link.includes('?') ? '&' : '?';
            return `${link}${separator}cardRegistered=true`;
        }
    }

    resetForm() {
        this.myForm.reset();
        this.orderLink = null;
        this.errorMessage = null;
        this.ngOnInit(); // Reset to default values
    }

    copyLink() {
        if (this.orderLink) {
            const url = this.appendCardRegisteredQuery(this.orderLink);
            navigator.clipboard.writeText(url).then(() => {
                // You could add a toast notification here
                console.log('Link copied to clipboard');
            });
        }
    }
} 